import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from "react";
import { Box, Button } from "@mui/material";
import "remirror/styles/all.css";
import "./remirror.scss";
import {
  OnChangeHTML,
  Remirror,
  useChainedCommands,
  useRemirror,
  useRemirrorContext,
} from "@remirror/react";
import { HtmlEditorMenu } from "./HtmlEditorMenu";
import { HtmlEditorProps } from "./editor.context";
import { editorExtensions } from "./editor.extensions";
import { HtmlEditorRef, EDITOR_MODE_EVENT, type EditorMode, getStoredEditorMode, setStoredEditorMode } from "./editor-func";
import { useHtmlEditorContext } from "./useHtmlEditorContext";
import { HtmlEditorBox } from "./HtmlEditorBox";
import { HtmlEditorProvider } from "./editor.provider";
import { IconTypes } from "../icons.types";
import { useDialogs } from "../../contexts/useDialogs";
import { devLog } from "../../services/util";
import { RawHtmlEditor } from "./RawHtmlEditor";

// Update HtmlEditorProps to include new options
export interface EnhancedHtmlEditorProps extends HtmlEditorProps {
  compact?: boolean;
  disableExternalEdit?: boolean;
  ultraCompact?: boolean;
  /** When true, mode toggle only affects this editor (not global) */
  localMode?: boolean;
  /** Called when editor mode changes */
  onModeChange?: (mode: EditorMode) => void;
}

export const InnerEditor = (props: HtmlEditorProps) => {
  const context = useHtmlEditorContext();
  return (
    <>
      <OnChangeHTML onChange={context.onChange} />
      {props.children}
    </>
  );
};

const ImperativeHandle = forwardRef(
  (_: unknown, ref: React.Ref<HtmlEditorRef>) => {
    const { setContent, manager, commands } = useRemirrorContext();

    const replaceContent = (content: string) => {
      manager.view.updateState(manager.createState({ content: content }));
    };

    const chain = useChainedCommands();

    useImperativeHandle(ref, () => ({
      setContent,
      replaceContent,
      focus: commands.focus,
      insertText: commands.insertText,
      insertParagraph: commands.insertParagraph,
      chain: chain,
    }));

    return <></>;
  }
);

export const HtmlEditorWrapperSwitch = forwardRef(
  (props: EnhancedHtmlEditorProps, ref: React.Ref<HtmlEditorRef>) => {
    if (props.value === null || props.value === undefined) {
      return <>{props.children}</>;
    }
    return <HtmlEditorWrapper {...props} ref={ref} />;
  }
);

export const HtmlEditorWrapper = forwardRef(
  (props: EnhancedHtmlEditorProps, ref: React.Ref<HtmlEditorRef>) => {
    // Create a key that changes when switching modes to force Remirror recreation
    const [remirrorKey, setRemirrorKey] = useState(0);

    const { manager, state } = useRemirror({
      extensions: editorExtensions,
      content: props.value,
      selection: "start",
      stringHandler: "cmCode",
      extraAttributes: [
        {
          identifiers: "nodes",
          attributes: {
            "data-expr-start": { default: null },
            "data-expr-end": { default: null },
            "data-section": { default: null },
          },
        },
        {
          identifiers: ["table", "tableRow", "tableCell", "tableHeaderCell"],
          attributes: { style: { default: null } },
        },
      ],
    });

    useEffect(() => {
      devLog(
        `remirror-test: ⚡ Remirror context created for: ${props.editorId}, key: ${remirrorKey}`
      );
    }, [props.editorId, remirrorKey]);

    // Listen for mode switches and recreate Remirror context
    useEffect(() => {
      const handleModeSwitch = () => {
        devLog(
          `remirror-test: 🔄 Recreating Remirror context for: ${props.editorId}`
        );
        setRemirrorKey((prev) => prev + 1);
      };

      // Listen for custom event from HtmlEditorMenu
      window.addEventListener(
        `remirror-recreate-${props.editorId}`,
        handleModeSwitch
      );

      return () => {
        window.removeEventListener(
          `remirror-recreate-${props.editorId}`,
          handleModeSwitch
        );
      };
    }, [props.editorId]);

    return (
      <Remirror key={remirrorKey} manager={manager} initialContent={state}>
        <HtmlEditorProvider {...props}>
          <InnerEditor {...props} />
          <ImperativeHandle ref={ref} />
        </HtmlEditorProvider>
      </Remirror>
    );
  }
);

// Ultra-compact mode that shows just a button to open editor in popup
export const UltraCompactHtmlEditor = (props: EnhancedHtmlEditorProps) => {
  const { openHtmlEditorModal } = useDialogs();

  const handleEdit = () => {
    openHtmlEditorModal({
      value: props.value,
      onChange: props.onChange,
      fields: props.fields,
      fieldArgs: props.fieldArgs,
      editorId: `ultracompact-${props.editorId}`,
    });
  };

  // Show content preview with edit button
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 1,
        position: "relative",
        minHeight: "36px",
        cursor: "pointer",
        "&:hover": {
          borderColor: "primary.main",
        },
      }}
      onClick={handleEdit}
    >
      <Box
        sx={{
          fontSize: "0.875rem",
          maxHeight: "60px",
          overflow: "hidden",
          pr: 8,
          "& p": {
            m: 0,
          },
        }}
      >
        {props.value ? (
          <div dangerouslySetInnerHTML={{ __html: props.value }} />
        ) : (
          <Box sx={{ color: "text.secondary", fontStyle: "italic" }}>
            Click to edit
          </Box>
        )}
      </Box>

      <Button
        size="small"
        variant="contained"
        color="primary"
        startIcon={<IconTypes.Edit />}
        sx={{
          position: "absolute",
          right: 8,
          top: "50%",
          transform: "translateY(-50%)",
          minWidth: 0,
          p: "4px 8px",
        }}
        onClick={handleEdit}
      >
        Edit
      </Button>
    </Box>
  );
};

export const HtmlEditor = (props: EnhancedHtmlEditorProps) => {
  const { compact, ultraCompact, disableExternalEdit, localMode, onModeChange, ...restProps } =
    props;
  const { openHtmlEditorModal } = useDialogs();

  // Editor mode: "wysiwyg" (default) or "html" (raw HTML editing)
  const [editorMode, setEditorMode] = useState<EditorMode>(getStoredEditorMode);
  // Track the global default mode (from localStorage / events)
  const [globalDefault, setGlobalDefault] = useState<EditorMode>(getStoredEditorMode);

  // Notify parent of mode changes
  useEffect(() => {
    onModeChange?.(editorMode);
  }, [editorMode, onModeChange]);

  // Listen for global mode changes from other editors
  useEffect(() => {
    const handleGlobalModeChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const mode = detail.mode as EditorMode;
      // Only update globalDefault when explicitly pinned
      if (detail.pin) {
        setGlobalDefault(mode);
      }
      if (!localMode) {
        setEditorMode(mode);
      }
    };
    window.addEventListener(EDITOR_MODE_EVENT, handleGlobalModeChange);
    return () => {
      window.removeEventListener(EDITOR_MODE_EVENT, handleGlobalModeChange);
    };
  }, [localMode]);

  // Whether current mode matches the global default
  const isDefaultMode = editorMode === globalDefault;

  // Detect container width for auto-compact mode
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoCompact, setAutoCompact] = useState(false);

  // Create a unique editor ID to prevent duplicate getRootProps
  const uniqueEditorId = useRef(
    `${props.editorId}-${Math.random().toString(36).substring(2, 9)}`
  );

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    let settled = false;

    const checkWidth = () => {
      // ResizeObserver can fire after the container ref has been nulled (unmount race).
      // Optional chaining keeps `width` undefined in that case; `width > 0` then evaluates false.
      const width = containerRef.current?.offsetWidth;
      if (width && width > 0) {
        setAutoCompact(width < 500);
        settled = true;
      }
    };

    checkWidth();

    // Add resize listener
    const resizeObserver = new ResizeObserver(() => checkWidth());
    const currentContainer = containerRef.current;
    resizeObserver.observe(currentContainer);

    // Poll until we get a real width (handles dialog open transitions)
    const interval = setInterval(() => {
      checkWidth();
      if (settled) clearInterval(interval);
    }, 100);
    const timeout = setTimeout(() => clearInterval(interval), 2000);

    return () => {
      if (currentContainer) {
        resizeObserver.unobserve(currentContainer);
      }
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [props.editorId]);

  if (ultraCompact) {
    return <UltraCompactHtmlEditor {...props} />;
  }

  const useCompactMode = compact || autoCompact;

  const switchToHtml = () => {
    setEditorMode("html");
    if (!localMode) {
      setStoredEditorMode("html");
      window.dispatchEvent(
        new CustomEvent(EDITOR_MODE_EVENT, { detail: { mode: "html" } })
      );
    }
  };

  const switchToWysiwyg = () => {
    setEditorMode("wysiwyg");
    if (!localMode) {
      setStoredEditorMode("wysiwyg");
      window.dispatchEvent(
        new CustomEvent(EDITOR_MODE_EVENT, { detail: { mode: "wysiwyg" } })
      );
    }
  };

  // Pin/toggle global default mode
  const pinCurrentMode = () => {
    // If current mode is already the default, toggle to the opposite
    const newDefault =
      isDefaultMode
        ? editorMode === "html"
          ? "wysiwyg"
          : "html"
        : editorMode;
    setStoredEditorMode(newDefault);
    setGlobalDefault(newDefault);
    if (!localMode) {
      setEditorMode(newDefault);
    }
    window.dispatchEvent(
      new CustomEvent(EDITOR_MODE_EVENT, {
        detail: { mode: newDefault, pin: true },
      })
    );
  };

  const handleExternalEdit = () => {
    openHtmlEditorModal({
      value: props.value,
      onChange: props.onChange,
      fields: props.fields,
      fieldArgs: props.fieldArgs,
      editorId: `external-${props.editorId}`,
    });
  };

  // HTML mode: raw textarea editor (bypasses ProseMirror entirely)
  if (editorMode === "html") {
    return (
      <Box ref={containerRef} sx={{ width: "100%" }}>
        <RawHtmlEditor
          value={props.value}
          onChange={props.onChange}
          onSwitchToWysiwyg={switchToWysiwyg}
          onExternalEdit={!disableExternalEdit ? handleExternalEdit : undefined}
          disableExternalEdit={disableExternalEdit}
          onPinMode={pinCurrentMode}
          isDefaultMode={isDefaultMode}
          fields={props.fields}
          fieldArgs={props.fieldArgs}
          compact={useCompactMode}
          maxHeight={props.maxHeight}
          fullScreen={props.fullScreen}
        />
      </Box>
    );
  }

  // WYSIWYG mode: current ProseMirror/Remirror editor
  return (
    <Box ref={containerRef} sx={{ width: "100%" }}>
      <HtmlEditorWrapper {...restProps} editorId={uniqueEditorId.current}>
        <Box>
          <HtmlEditorMenu
            editorId={uniqueEditorId.current}
            compact={useCompactMode}
            disableExternalEdit={disableExternalEdit}
            onExternalEdit={props.onChange}
            onSwitchToHtml={switchToHtml}
            onPinMode={pinCurrentMode}
            isDefaultMode={isDefaultMode}
          />
        </Box>

        <HtmlEditorBox
          onSourceEdit={props.onChange}
          editorId={uniqueEditorId.current}
          compact={useCompactMode}
          maxHeight={props.maxHeight}
        />
      </HtmlEditorWrapper>
    </Box>
  );
};

ImperativeHandle.displayName = "ImperativeHandle";
HtmlEditorWrapper.displayName = "HtmlEditorWrapper";
HtmlEditorWrapperSwitch.displayName = "HtmlEditorWrapperSwitch";
